import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { apiFetch, mediaUrl, getVisitorKey } from "../utils/api";

const AUTO_ADVANCE_MS = 3500;
const SWIPE_THRESHOLD = 45;

/* ─── ONE FIXED BOX, PER-BANNER FIT ───────────────────────────────────────────
 * Every banner renders in the SAME box: a fixed 1600/780 ratio (--bn-ratio in CSS),
 * scaling with the column width. The box no longer follows the image, so the hero
 * cannot change height as the carousel advances. frame_width / frame_height are still
 * returned by the API but no longer drive layout.
 *
 * What the author picks per banner is image_fit — how the image sits inside that box:
 *   'cover'   (default) fill the box, cropping the overflow
 *   'contain'           show the WHOLE image, letterboxed on the neutral background
 * Fit exists because a fixed box crops tall images, and some banners lose important
 * content that way. It changes only the image — never the box.
 *
 * Overlays (buttons, poll, yes/no) are positioned as a PERCENTAGE of the box with
 * translate(-50%, -50%). Because the box is now the same shape for every banner, a
 * button dropped at 25%/78% in the dAdmin editor lands at exactly 25%/78% here — which
 * per-banner box sizes could not guarantee. image_fit does not affect this: it moves
 * pixels inside the box, not the box the percentages resolve against.
 */

/**
 * Anything that isn't 'contain' is 'cover' — matching the column default, so an
 * unrecognised or missing value can never letterbox a banner unexpectedly.
 */
const fitOf = (banner) =>
    String(banner?.image_fit || "").toLowerCase() === "contain" ? "contain" : "cover";
const BannerCarousel = ({ isAuthenticated, onCountChange }) => {
    const [allBanners, setAllBanners] = useState(null); // null = loading
    const [broken, setBroken] = useState({});
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [voting, setVoting] = useState(false);

    const touchStartX = useRef(null);
    const regionRef = useRef(null);

    /* ─── DATA ─────────────────────────────────────────────────────────────── */

    const load = useCallback(async () => {
        try {
            const visitorKey = isAuthenticated ? null : getVisitorKey();
            const qs = visitorKey ? `?visitor_key=${encodeURIComponent(visitorKey)}` : "";
            const res = await apiFetch(`/api/Dolluzcorp/banner/list${qs}`);
            const data = await res.json();
            setAllBanners(data.success ? data.data : []);
        } catch (err) {
            console.error("Failed to load banners", err);
            setAllBanners([]);
        }
    }, [isAuthenticated]);

    useEffect(() => { load(); }, [load]);

    /**
     * A banner whose image 404s is dropped entirely, rather than shown as an empty box
     * with its buttons floating over nothing. The overlays are meaningless without the
     * picture they were positioned against.
     */
    const banners = allBanners?.filter((b) => !broken[b.banner_id]) ?? null;
    const count = banners?.length || 0;

    // Dropping a banner can strand the index past the end of the list.
    useEffect(() => {
        if (count && index >= count) setIndex(0);
    }, [count, index]);

    /**
     * Tell the parent how many banners this viewer actually sees, so the hero can drop
     * its banner column and let the copy take the full width when there are none.
     * Reports null while loading (unknown) vs 0 (known-empty), so the hero doesn't
     * collapse prematurely during the first fetch.
     */
    useEffect(() => {
        if (!onCountChange) return;
        onCountChange(allBanners === null ? null : count);
    }, [allBanners, count, onCountChange]);

    /* ─── AUTO-ADVANCE ─────────────────────────────────────────────────────── */
    // Pauses on hover and on keyboard focus, and never runs for a single banner.

    useEffect(() => {
        if (count < 2 || paused) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % count), AUTO_ADVANCE_MS);
        return () => clearInterval(t);
    }, [count, paused]);

    const go = useCallback((next) => {
        if (!count) return;
        setIndex(((next % count) + count) % count);
    }, [count]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1); }
        if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1); }
    };

    const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };

    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > SWIPE_THRESHOLD) go(index + (dx < 0 ? 1 : -1));
        touchStartX.current = null;
    };

    /* ─── VOTING ───────────────────────────────────────────────────────────── */

    const vote = async (banner, optionId) => {
        if (voting) return;
        setVoting(true);
        try {
            const res = await apiFetch("/api/Dolluzcorp/banner/vote", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    banner_id: banner.banner_id,
                    option_id: optionId,
                    visitor_key: isAuthenticated ? undefined : getVisitorKey(),
                }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message);

            setAllBanners((prev) => prev.map((b) => (
                b.banner_id === banner.banner_id
                    ? { ...b, options: data.data.options, total_votes: data.data.total_votes, my_vote: data.data.my_vote }
                    : b
            )));
        } catch (err) {
            console.error("Vote failed", err);
        } finally {
            setVoting(false);
        }
    };

    /* ─── RENDER ───────────────────────────────────────────────────────────── */

    if (allBanners === null) {
        return <div className="sk carousel-skeleton" aria-hidden="true" />;
    }

    // No banners — or none whose image loads. The hero copy takes the full width.
    if (!count) return null;

    /**
     * ONE FIXED BOX FOR EVERY BANNER.
     *
     * The box used to take each banner's own frame_width × frame_height, which meant the
     * hero changed height every time the carousel advanced. The shape is now a single
     * constant (--bn-ratio in CSS, 1600/780 — the proportion the Dolluz Corp logo banner
     * already rendered at), so the height is fully predictable from the column width.
     *
     * Consequences, both intended:
     *   - The image is cropped to fill (object-fit: cover). Losing the edges of an
     *     oddly-shaped image is preferred over letterboxing or a moving box.
     *   - Overlays stay a % of this box. Because the box is now the SAME shape on every
     *     banner, a position authored in dAdmin lands identically here — which the
     *     per-banner box could not guarantee.
     *
     * frame_width / frame_height are still selected by the API but no longer drive layout.
     *
     * What the author DOES still control per banner is image_fit — how the image sits in
     * that shared box. It changes nothing about the box or the overlay percentages.
     */
    return (
        <section
            className="carousel"
            ref={regionRef}
            aria-roledescription="carousel"
            aria-label="Featured banners"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onKeyDown={onKeyDown}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Fixed shape for every slide — the ratio lives in CSS (--bn-ratio), so the
                height is always width ÷ ratio and never depends on which image loaded. */}
            <div className="carousel-viewport">
                {banners.map((banner, i) => {
                    const src = mediaUrl(banner.banner_image);
                    const isCurrent = i === index;
                    const votable = banner.banner_type === "poll" || banner.banner_type === "yesno";
                    const voted = banner.my_vote != null;

                    return (
                        <div
                            key={banner.banner_id}
                            className={`carousel-slide${isCurrent ? " current" : ""}`}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={`${i + 1} of ${count}`}
                            aria-hidden={!isCurrent}
                            // Off-screen slides are inert, so their overlay buttons are not
                            // tabbable. NOTE: this must be a real boolean — React 19 treats
                            // inert="" as FALSE, which silently left hidden slides focusable.
                            inert={!isCurrent}
                        >
                            <img
                                className="carousel-image"
                                src={src}
                                alt={banner.title || ""}
                                onError={() => setBroken((prev) => ({ ...prev, [banner.banner_id]: true }))}
                                loading={i === 0 ? "eager" : "lazy"}
                                decoding="async"
                                /* How THIS image sits in the shared box — the box itself is
                                   unchanged. 'contain' keeps the whole image (letterboxed);
                                   anything else fills and crops. */
                                style={{ objectFit: fitOf(banner), objectPosition: "center" }}
                            />

                            <div className="overlay-layer">
                                {banner.banner_type === "buttons" && banner.buttons.map((btn) => (
                                    <a
                                        key={btn.button_id}
                                        className="overlay-btn"
                                        href={btn.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            left: `${btn.pos_x}%`,
                                            top: `${btn.pos_y}%`,
                                            transform: "translate(-50%, -50%)",
                                            background: btn.bg_color,
                                            color: btn.text_color,
                                        }}
                                    >
                                        {btn.label}
                                    </a>
                                ))}

                                {votable && banner.options.length > 0 && (
                                    <div
                                        className="poll"
                                        style={{
                                            left: `${banner.overlay_x}%`,
                                            top: `${banner.overlay_y}%`,
                                            transform: "translate(-50%, -50%)",
                                        }}
                                    >
                                        {banner.title && <p className="poll-q">{banner.title}</p>}

                                        <div className={`poll-options ${banner.options_layout === "vertical" ? "vertical" : "horizontal"}`}>
                                            {banner.options.map((opt) => {
                                                const chosen = banner.my_vote === opt.option_id;
                                                return (
                                                    <button
                                                        key={opt.option_id}
                                                        type="button"
                                                        className={`poll-option${chosen ? " chosen" : ""}`}
                                                        onClick={() => vote(banner, opt.option_id)}
                                                        disabled={voting}
                                                        aria-pressed={chosen}
                                                    >
                                                        <span className="poll-label">{opt.option_text}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Confirmation only — the tally and per-option percentages are
                                            deliberately NOT shown to voters. The counts are still recorded
                                            server-side (dAdmin reports them); the public banner just doesn't
                                            reveal them. Which option they picked stays visible via .chosen. */}
                                        {voted && (
                                            <p className="poll-total" aria-live="polite">
                                                Thanks for voting
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {count > 1 && (
                <>
                    <button type="button" className="carousel-arrow prev" onClick={() => go(index - 1)} aria-label="Previous banner">
                        <FaChevronLeft />
                    </button>
                    <button type="button" className="carousel-arrow next" onClick={() => go(index + 1)} aria-label="Next banner">
                        <FaChevronRight />
                    </button>

                    <div className="carousel-dots" role="tablist" aria-label="Choose banner">
                        {banners.map((b, i) => (
                            <button
                                key={b.banner_id}
                                type="button"
                                role="tab"
                                className={`carousel-dot${i === index ? " active" : ""}`}
                                aria-selected={i === index}
                                aria-label={`Banner ${i + 1}`}
                                onClick={() => go(i)}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};

export default BannerCarousel;
